import { useState, useEffect, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { X } from 'lucide-react';
import { useDialogA11y } from '../ui/useDialogA11y';

interface Course {
  id: string;
  name: string;
  status: string;
}

interface CourseSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectCourse: (courseId: string) => void;
}

export function CourseSelectionModal({ isOpen, onClose, onSelectCourse }: CourseSelectionModalProps) {
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedCourseId, setSelectedCourseId] = useState<string>('');
  const dialogRef = useRef<HTMLDivElement>(null);
  useDialogA11y(isOpen, onClose, dialogRef);

  useEffect(() => {
    if (isOpen) {
      loadCourses();
    }
  }, [isOpen]);

  const loadCourses = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('courses')
        .select('id, name, status')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setCourses(data || []);
    } catch (error) {
      console.error('Error loading courses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = () => {
    if (selectedCourseId) {
      onSelectCourse(selectedCourseId);
      onClose();
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      active: { label: 'Activo', className: 'bg-green-500/10 text-green-400 border-green-500/30' },
      inactive: { label: 'Inactivo', className: 'bg-gray-500/10 text-muted border-gray-500/30' },
      completed: { label: 'Completado', className: 'bg-blue-500/10 text-blue-400 border-blue-500/30' },
      draft: { label: 'Borrador', className: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30' },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.inactive;

    return (
      <span className={`px-2 py-1 rounded-md border text-xs font-medium ${config.className}`}>
        {config.label}
      </span>
    );
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      role="presentation"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title-courseselection"
        tabIndex={-1}
        className="glass-deep rounded-xl w-full max-w-2xl max-h-[80vh] overflow-hidden border border-white/[0.06] shadow-2xl"
      >
        <div className="flex justify-between items-center p-6 border-b border-white/[0.06]">
          <div>
            <h2 id="modal-title-courseselection" className="text-display text-2xl font-bold text-white">Seleccionar Curso</h2>
            <p className="text-muted text-sm mt-1">
              Elige el curso al que deseas asignar los materiales
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
          >
            <X className="w-6 h-6 text-muted" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(80vh-180px)]">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cubus-lime"></div>
            </div>
          ) : courses.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted">No hay cursos disponibles</p>
            </div>
          ) : (
            <div className="space-y-3">
              {courses.map((course) => (
                <label
                  key={course.id}
                  className={`flex items-center justify-between p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    selectedCourseId === course.id
                      ? 'border-cubus-lime bg-cubus-lime/5'
                      : 'border-white/[0.08] hover:border-white/[0.1] bg-[#14161A]'
                  }`}
                >
                  <div className="flex items-center gap-4 flex-1">
                    <input
                      type="radio"
                      name="course"
                      value={course.id}
                      checked={selectedCourseId === course.id}
                      onChange={(e) => setSelectedCourseId(e.target.value)}
                      className="w-5 h-5 text-cubus-lime focus:ring-cubus-lime focus:ring-offset-0 bg-gray-700 border-white/[0.1]"
                    />
                    <div className="flex-1">
                      <h3 className="text-white font-semibold">{course.name}</h3>
                    </div>
                    {getStatusBadge(course.status)}
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 p-6 border-t border-white/[0.06]">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-all font-medium"
          >
            Cancelar
          </button>
          <button
            onClick={handleConfirm}
            disabled={!selectedCourseId}
            className="px-6 py-2 bg-cubus-lime text-cubus-bg rounded-lg hover:bg-cubus-lime-dim transition-all font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Continuar
          </button>
        </div>
      </div>
    </div>
  );
}
